import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Pagination,
  TextField,
  Typography,
  Paper,
  Avatar,
  Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  addScooterNoteAsync,
  getScooterNotesAsync
} from '../../../redux/slices/scootersSlice';
import moment from 'moment';

interface Note {
  _id: string;
  text: string;
  adminLogin?: string;
  createdAt: string;
  updatedAt: string;
}

interface NotesListResponse {
  data: { notes: Note[] };
  pagination: { totalPages: number };
}

interface Props {
  scooterId?: string;
  open: boolean;
}

const NotesContent = ({ scooterId, open }: Props) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [data, setData] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState<string>('');

  const getNotes = () => {
    if (!scooterId) return;
    
    getScooterNotesAsync({
      scooterId,
      page,
      limit: 20
    }).then((res: NotesListResponse) => {
      const notes = res.data.notes ?? [];
      notes.sort(
        (a: Note, b: Note) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setData(notes);
      setPages(res.pagination.totalPages ?? 1);
    });
  };

  useEffect(() => {
    if (open && scooterId) {
      getNotes();
    } else {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, scooterId]);

  const addNewNote = () => {
    if (!scooterId || !newNote.trim()) return;
    
    addScooterNoteAsync({ scooterId, text: newNote }).then(() => {
      getNotes();
      setNewNote('');
    });
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getAdminInitial = (adminLogin?: string) => {
    if (adminLogin && adminLogin.length > 0) {
      return adminLogin.charAt(0).toUpperCase();
    }
    return 'A';
  };

  return (
    <Box>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={9}>
          <TextField
            name='note'
            label={t('scooterModal.newNote')}
            fullWidth
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                addNewNote();
              }
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Button 
            color='primary' 
            onClick={addNewNote}
            fullWidth
            variant="contained"
            disabled={!newNote.trim()}
          >
            {t('scooterModal.addNote')}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {data.length === 0 ? (
          <Typography textAlign='center' sx={{ py: 4 }}>
            {t('scooterModal.noNotes')}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {data.map((note) => {
              const adminInitial = getAdminInitial(note.adminLogin);
              const avatarColor = getAvatarColor(note.adminLogin || 'A');
              
              return (
                <Grid item xs={12} key={note._id}>
                  <Paper sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: avatarColor,
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {adminInitial}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {note.adminLogin || t('scooterModal.administrator')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {moment(note.createdAt).format('DD MMM YYYY, HH:mm')}
                          </Typography>
                        </Stack>
                        <Typography variant="body2">
                          {note.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default NotesContent;
